// LoginPage.jsx
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap } from 'lucide-react';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { Spinner } from 'react-bootstrap';
import AppContext from '../../../../context/AppContext';
import '../../../../assets/css/student/dashboard/Auth.css';

function LoginPage() {
    const navigate = useNavigate();
    const { baseUrl, regExpChecker } = useContext(AppContext);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');

    useEffect(() => {
        const studentEmail = localStorage.getItem('studentEmail');
        const studentPassword = localStorage.getItem('studentPassword');
        if (studentEmail && studentPassword) {
            setIsLoading(true);
            studentAutoLogin(studentEmail, studentPassword);
        }
    }, []);

    const studentAutoLogin = (studentEmail, studentPassword) => {
        setIsLoading(true);
        const formdata = new FormData();
        formdata.append('email', studentEmail);
        formdata.append('password', studentPassword);

        axios
            .post(`${baseUrl}/student/auth/studentlogin`, formdata)
            .then((response) => {
                if (response.data.error) {
                    setErrorMessage(response.data.error);
                    setIsLoading(false);
                } else if (response.data.student) {
                    localStorage.setItem('studentEmail', response.data.student.email);
                    localStorage.setItem('studentPassword', response.data.student.password);
                    navigate('/student/dashboard');
                } else {
                    setErrorMessage('Invalid email or password');
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                setErrorMessage(error.response?.data?.message || 'Something went wrong!');
                setIsLoading(false);
            });
    };

    const studentLogin = () => {
        setIsLoading(true);
        const formdata = new FormData();
        formdata.append('email', email);
        formdata.append('password', password);

        axios
            .post(`${baseUrl}/student/auth/studentlogin`, formdata)
            .then((response) => {
                if (response.data.error) {
                    setErrorMessage(response.data.error);
                    setIsLoading(false);
                } else if (response.data.student) {
                    localStorage.setItem('studentEmail', response.data.student.email);
                    localStorage.setItem('studentPassword', response.data.student.password);
                    navigate('/student/dashboard');
                } else {
                    setErrorMessage('Invalid email or password');
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                setErrorMessage(error.response?.data?.message || 'Something went wrong!');
                setIsLoading(false);
            });
    };

    return isLoading ? (
        <div className="sta-loading-screen">
            <Spinner style={{ height: 50, width: 50 }} />
        </div>
    ) : (
        <div className="sta-auth-page">

            {/* ── LEFT PANEL ── */}
            <div className="sta-left-panel">
                <div className="sta-left-blob sta-left-blob--top" />
                <div className="sta-left-blob sta-left-blob--bottom" />
                <div className="sta-left-content">
                    <div className="sta-left-logo">
                        <GraduationCap size={34} />
                    </div>
                    <h1 className="sta-left-brand">Student Support Hub</h1>
                    <p className="sta-left-tagline">
                        Your academic journey starts here. Sign in to access resources, support, and more.
                    </p>
                    <div className="sta-left-deco">
                        <span />
                        <span />
                        <span />
                    </div>
                </div>
            </div>

            {/* ── RIGHT PANEL ── */}
            <div className="sta-right-panel">
                <div className="sta-form-box sta-fade-in">
                    <div className="sta-form-header">
                        <h2 className="sta-form-title">Welcome back</h2>
                        <p className="sta-form-sub">Sign in to your student account</p>
                    </div>

                    <form className="sta-form" onSubmit={(e) => e.preventDefault()}>

                        {/* Email */}
                        <div className="sta-field">
                            <label className="sta-label">Email</label>
                            <div className="sta-input-wrap">
                                <FaEnvelope className="sta-icon" />
                                <input
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setEmailError(regExpChecker('email', e.target.value));
                                    }}
                                    className={`sta-input ${emailError ? 'sta-input--error' : ''}`}
                                    autoComplete="off"
                                />
                            </div>
                            {emailError && <p className="sta-field-error">{emailError}</p>}
                        </div>

                        {/* Password */}
                        <div className="sta-field">
                            <label className="sta-label">Password</label>
                            <div className="sta-input-wrap">
                                <FaLock className="sta-icon" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError(regExpChecker('password', e.target.value));
                                    }}
                                    className={`sta-input ${passwordError ? 'sta-input--error' : ''}`}
                                    autoComplete="off"
                                />
                                <button
                                    type="button"
                                    className="sta-eye-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            {passwordError && <p className="sta-field-error">{passwordError}</p>}
                        </div>

                        <div className="sta-forgot-row">
                            <span className="sta-forgot-link">Forgot password?</span>
                        </div>

                        {errorMessage && (
                            <div className="sta-global-error">{errorMessage}</div>
                        )}

                        <button type="button" className="sta-submit-btn" onClick={studentLogin}>
                            Login
                        </button>
                    </form>

                    <div className="sta-switch-row">
                        <span>Don't have an account?</span>
                        <a
                            className="sta-switch-link"
                            onClick={() => navigate('/student/signup')}
                            style={{ cursor: 'pointer' }}
                        >
                            Register
                        </a>
                    </div>
                </div>
            </div>

        </div>
    );
}

export default LoginPage;